import {DefaultPluginConfig, registerPlugin} from "../lib/plugin";

const spritesheet = '/plugins/res/heart-spritesheet.png';

interface HeartsConfig extends DefaultPluginConfig {
    location: 'left'|'right'
    horizontalPercent: number
    duration: number
    minSize: number
    maxSize: number
    volume: number
}

registerPlugin<HeartsConfig>('hearts', 'Hearts for tipping', {
    defaultSettings: [ "location", "horizontalPercent", "duration" ],

    settings: {
        location: {
            title: 'Location',
            type: 'select',
            options: ['left', 'right'],
            defaultValue: 'right',
        },
        horizontalPercent: {
            title: 'Width',
            help: 'Used portion of screen width in %',
            type: 'number',
            defaultValue: 20,
        },
        duration: {
            title: 'Duration',
            help: 'Duration to move a heart from top to bottom (in seconds)',
            type: 'number',
            defaultValue: 2.5,
        },
        minSize: {
            title: 'Min. size',
            help: 'Minimum size of one heart',
            type: 'number',
            defaultValue: 20,
        },
        maxSize: {
            title: 'Max. size',
            help: 'Maximum size of one heart',
            type: 'number',
            defaultValue: 30,
        },
        volume: {
            title: 'Sound volume',
            help: 'Volume of the ping sound (0–100)',
            type: 'number',
            defaultValue: 10,
        },
    },

    testAction: {
        tip: {
            title: 'Test Tipping',
            config: {
                username: {
                    title: 'Username',
                    type: 'string',
                    defaultValue: 'Test',
                },
                amount: {
                    title: 'Amount',
                    type: 'number',
                    defaultValue: 10,
                },
            },
            run: (data, config) => {
                addHearts(data.amount, config)
            }
        }
    },

    initialize: (id: string, config) => {
        console.log('hearts plugin init', id, config)
        if (!document.getElementById('hearts-style')) {
            const style = document.createElement('style');
            style.id = 'hearts-style';
            style.innerHTML = `
                @keyframes heart-spritesheet {
                    from { background-position: 0 0; }
                    to { background-position: -14400px 0; }
                }
            `;
            document.head.appendChild(style);
        }
    },

    onConfig: (id: string, config) => {
        console.log('hearts plugin config', id, config)
    },

    onWsAction: (id: string, action: string, data: any, config: any) => {
        if (['tip', 'hearts'].includes(action)) {
            console.log('adding hearts with config', id, config)
            addHearts(data.amount, config)
            return true
        }
        return false
    }
})

function randomInteger(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

const SPRITE_FRAMES = 240
const SPRITE_LOOP_MS = 2000

class Heart {
    private config: { duration: number; horizontalPercent: number; location: string; minSize: number; maxSize: number };
    private onFinished: any;
    private container: HTMLSpanElement|null;
    private wrapper: HTMLDivElement|null;
    private spriteInterval: ReturnType<typeof setInterval>|null;

    constructor(onFinished, config = {}) {
        this.config = {
            ...{
                location: 'right',
                duration: 2.5,
                minSize: 20,
                maxSize: 30,
                horizontalPercent: 20,
            },
            ...config
        }

        this.config.duration = parseInt('' + this.config.duration) * 1000
        this.config.minSize = parseInt('' + this.config.minSize)
        this.config.maxSize = parseInt('' + this.config.maxSize)

        const speedMultiplier = randomInteger(95, 115) / 100
        this.config.duration = this.config.duration * speedMultiplier

        this.onFinished = onFinished

        this.container = null
        this.wrapper = null
        this.spriteInterval = null
    }

    attach() {
        const availableWidth = window.innerWidth * (this.config.horizontalPercent / 100)

        const size = Math.floor(Math.random() * this.config.maxSize) + this.config.minSize
        const position = Math.floor(Math.random() * availableWidth)
        const fallDuration = this.config.duration

        // Outer wrapper handles horizontal sway
        const swayAmplitude = randomInteger(20, 50)
        const swayDir = Math.random() > 0.5 ? 1 : -1
        this.wrapper = document.createElement('div')
        this.wrapper.classList.add('heart-wrapper')
        this.wrapper.style.setProperty('--sway-a', `${swayAmplitude * swayDir}px`)
        this.wrapper.style.setProperty('--sway-b', `${swayAmplitude * -swayDir * 0.7}px`)
        this.wrapper.style.setProperty('--sway-c', `${swayAmplitude * swayDir * 0.4}px`)
        this.wrapper.style.animation = `heart-sway ${fallDuration}ms ease-in-out`

        if (this.config.location === 'right') {
            this.wrapper.style.right = `${position}px`
        } else {
            this.wrapper.style.left = `${position}px`
        }

        // Inner span handles vertical fall with gravity (ease-in)
        this.container = document.createElement('span')
        this.container.classList.add('heart')
        this.container.style.visibility = 'hidden'
        this.container.style.width = `${size}px`
        this.container.style.height = `${size}px`
        this.container.style.backgroundImage = `url(${spritesheet})`
        this.container.style.backgroundSize = `${size * SPRITE_FRAMES}px ${size}px`
        this.container.style.backgroundPosition = '0 0'
        this.container.style.animation = `heart-fall ${fallDuration}ms ease-in`

        this.wrapper.appendChild(this.container)
        document.body.appendChild(this.wrapper)

        this.container.style.visibility = 'visible'

        let frame = 0
        this.spriteInterval = setInterval(() => {
            frame = (frame + 1) % SPRITE_FRAMES
            if (this.container) {
                this.container.style.backgroundPosition = `${-frame * size}px 0`
            }
        }, SPRITE_LOOP_MS / SPRITE_FRAMES)

        setTimeout(() => {
            clearInterval(this.spriteInterval)
            this.wrapper.parentNode.removeChild(this.wrapper)
            this.onFinished(this)
        }, fallDuration * 1.1)
    }
}

let hearts = []

const addHearts = async (num = 1, config = {
    placementTimeout: 0
}) => {
    const pings = Math.max(1, Math.floor(num / 10))
    const volume = Math.min(100, Math.max(0, Number((config as any).volume ?? 80))) / 100
    for (let i = 0; i < pings; i++) {
        setTimeout(() => {
            const audio = new Audio('/plugins/res/universfield-happy-message-ping-351298.mp3')
            audio.volume = volume
            audio.play()
        }, i * 100)
    }

    const durationMs = parseInt('' + ((config as any).duration ?? 2.5)) * 1000
    const placementTimeout = config.placementTimeout || Math.round(durationMs / 60)

    for (let i = 0; i < num; i++) {
        const heart = new Heart((h) => {
            hearts = hearts.filter(h2 => h2 !== h)
        }, config)

        hearts.push(heart)

        await new Promise((resolve) => setTimeout(() => {
            heart.attach()
            resolve(null)
        }, placementTimeout))
    }
}